import React, { useState, useRef, useMemo } from 'react';
import Draggable from 'react-draggable';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, Tooltip, TablePagination, TextField, TableSortLabel, Box, Typography
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import { enrichAssetsWithGroupSaleInfo, formatAssetDescription, isAssetSold } from '../../../Utils/assetSaleDisplayUtils';

function descendingComparator(a, b, orderBy) {
    if (a[orderBy] < b[orderBy]) return 1;
    if (a[orderBy] > b[orderBy]) return -1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilized = array.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
}

const formatDateShort = (d) =>
    !d ? '-' : new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

const SoldAssetsViewer = ({
    open = false,
    onClose = () => {},
    assets = [],
    assetGroups = [],
    assetRelations = [],
    onRowClick,
}) => {
    const { t } = useTranslation();
    const nodeRef = useRef(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('sellDate');
    const [searchTerm, setSearchTerm] = useState('');

    const groupMap = useMemo(() => {
        const map = {};
        (assetGroups || []).forEach(g => { map[g.assetGroupId] = g.name; });
        return map;
    }, [assetGroups]);

    const soldAssets = useMemo(() => {
        const enriched = enrichAssetsWithGroupSaleInfo(assets, assetRelations);
        return enriched.filter((a) => isAssetSold(a));
    }, [assets, assetRelations]);

    const missingAmountCount = useMemo(() => {
        return soldAssets.filter(
            (a) => !a.soldViaAssetGroup && (!a.sellAmount || a.sellAmount === 0)
        ).length;
    }, [soldAssets]);

    const filtered = useMemo(() => {
        if (!searchTerm.trim()) return soldAssets;
        const q = searchTerm.trim().toLowerCase();
        return soldAssets.filter(a => {
            const displayDescription = formatAssetDescription(a.description, a.sellDate, isAssetSold(a));
            return (
            (a.name || '').toLowerCase().includes(q) ||
            (a.assetNumber?.toString() || '').includes(q) ||
            (displayDescription || '').toLowerCase().includes(q) ||
            (groupMap[a.assetGroupId] || '').toLowerCase().includes(q) ||
            formatDateShort(a.sellDate).includes(q)
            );
        });
    }, [soldAssets, searchTerm, groupMap]);

    const sorted = useMemo(() => {
        if (!orderBy) return filtered;
        return stableSort(filtered, getComparator(order, orderBy));
    }, [filtered, order, orderBy]);

    const paginated = useMemo(() => {
        return sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    }, [sorted, page, rowsPerPage]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    if (!open) return null;

    return (
        <Draggable handle=".drag-handle-sold" nodeRef={nodeRef}>
            <div
                ref={nodeRef}
                style={{
                    position: 'absolute',
                    top: '10%',
                    left: '25%',
                    transform: 'translate(-50%, 0)',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '16px',
                    minWidth: '1000px',
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    zIndex: 1300,
                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.4)',
                    border: '1px solid #304FFF',
                }}
            >
                <div
                    className="drag-handle-sold"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'move',
                        marginBottom: 8,
                    }}
                >
                    <Tooltip title={t('Close')}>
                        <IconButton onClick={onClose}>
                            <CancelIcon style={{ color: '#304FFF' }} />
                        </IconButton>
                    </Tooltip>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                        {t('SoldAssets') || 'נכסים שנמכרו'}
                    </div>
                </div>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 2 }}>
                    <TextField
                        placeholder={t('Search') || 'חיפוש'}
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                        variant="standard"
                        InputProps={{
                            disableUnderline: true,
                            sx: {
                                height: '40px',
                                width: '250px',
                                padding: '0 10px',
                                borderRadius: '8px',
                                background: '#F5F5F6',
                                border: '1px solid transparent',
                                '&:focus-within': { border: '2px solid #304FFF' },
                            },
                        }}
                        inputProps={{ style: { textAlign: 'right', direction: 'rtl' } }}
                    />
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#333' }}>
                            {t('TotalSold') || 'סה"כ נמכרו'}: <strong>{soldAssets.length}</strong>
                        </Typography>
                        {missingAmountCount > 0 && (
                            <Typography variant="body2" sx={{ color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <WarningAmberIcon sx={{ fontSize: 16 }} />
                                {t('MissingSellAmount') || 'חסר סכום מכירה'}: <strong>{missingAmountCount}</strong>
                            </Typography>
                        )}
                    </Box>
                </Box>

                <TableContainer component={Paper} style={{ maxHeight: '400px' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {[
                                    { id: 'assetNumber', label: t('AssetNumber') || 'מספר נכס' },
                                    { id: 'name', label: t('AssetName') || 'שם נכס' },
                                    { id: 'purchaseDate', label: t('PurchaseDate') || 'תאריך רכישה' },
                                    { id: 'originalPrice', label: t('OriginalPrice') || 'מחיר מקורי' },
                                    { id: 'sellDate', label: t('SellDate') || 'תאריך מכירה' },
                                    { id: 'sellAmount', label: t('SellAmount') || 'סכום מכירה' },
                                    { id: 'assetGroupId', label: t('AssetGroup') || 'קבוצה' },
                                    { id: 'description', label: t('Description') || 'הערות' },
                                ].map(col => (
                                    <TableCell key={col.id} align="center" sx={{ fontWeight: 700 }}>
                                        <TableSortLabel
                                            active={orderBy === col.id}
                                            direction={orderBy === col.id ? order : 'asc'}
                                            onClick={() => handleRequestSort(col.id)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginated.map((asset) => {
                                const isLinkedChildSale = Boolean(asset.soldViaAssetGroup);
                                const isMissing = !isLinkedChildSale && (!asset.sellAmount || asset.sellAmount === 0);
                                const displayDescription = formatAssetDescription(
                                    asset.description,
                                    asset.sellDate,
                                    isAssetSold(asset)
                                );
                                return (
                                    <TableRow
                                        key={asset.id}
                                        hover
                                        onClick={() => onRowClick?.(asset.id, 'asset')}
                                        sx={{
                                            cursor: 'pointer',
                                            backgroundColor: isMissing ? 'rgba(211, 47, 47, 0.08)' : 'inherit',
                                            '&:hover': { backgroundColor: isMissing ? 'rgba(211, 47, 47, 0.14)' : undefined },
                                        }}
                                    >
                                        <TableCell align="center">{asset.assetNumber ?? '-'}</TableCell>
                                        <TableCell align="center">{asset.name ?? '-'}</TableCell>
                                        <TableCell align="center">{formatDateShort(asset.purchaseDate)}</TableCell>
                                        <TableCell align="center">{beautifyNumber(asset.originalPrice, 0)}</TableCell>
                                        <TableCell align="center">{formatDateShort(asset.sellDate)}</TableCell>
                                        <TableCell align="center">
                                            {isLinkedChildSale ? (
                                                '-'
                                            ) : isMissing ? (
                                                <Tooltip title={t('MissingSellAmount') || 'סכום מכירה חסר'} arrow>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: '#d32f2f' }}>
                                                        <WarningAmberIcon sx={{ fontSize: 16 }} />
                                                        <span>{t('Missing') || 'חסר'}</span>
                                                    </Box>
                                                </Tooltip>
                                            ) : (
                                                beautifyNumber(asset.sellAmount, 0)
                                            )}
                                        </TableCell>
                                        <TableCell align="center">{groupMap[asset.assetGroupId] || '-'}</TableCell>
                                        <TableCell align="center">{displayDescription || '-'}</TableCell>
                                    </TableRow>
                                );
                            })}
                            {paginated.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 3, color: '#999' }}>
                                        {t('NoSoldAssets') || 'אין נכסים שנמכרו'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={sorted.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    labelRowsPerPage={t('RowsPerPage') || 'שורות בעמוד'}
                />
            </div>
        </Draggable>
    );
};

export default SoldAssetsViewer;
